import React from "react";

import { cn } from "../../utils/cn";
import { forcedFieldBorderClasses } from "../ui/force-state";

export interface NumberFieldProps {
    /**
     * The numeric value to display. Formatted through `format` if given,
     * otherwise rendered as `String(value)`. The caller owns the value —
     * including any clamping applied before it comes back in as `value`.
     *
     * `null` renders the field EMPTY (showing `placeholder`, if any) — the
     * "no value yet, the user hasn't typed it" state. The caller decides when a
     * field is empty vs zero; passing `null` keeps a freshly-opened config field
     * blank instead of seeding a `0`.
     */
    value: number | null;
    /**
     * Called with the parsed number as the user types, or `null` when the
     * field is empty or the input doesn't parse to a number. The caller
     * decides the empty fallback and applies any domain clamping. Keeping
     * clamp/fallback in the caller is what makes this a domain-free control.
     */
    onChange: (value: number | null) => void;
    /** aria-label for the input. */
    ariaLabel: string;
    /**
     * Optional display formatter (e.g. thousands separators for a budget).
     * Applied to `value` for the input's displayed text only — the parsed
     * numeric value is still emitted raw via `onChange`.
     */
    format?: (value: number) => string;
    /**
     * Allow a single decimal point. Default false (digits only). When true,
     * `inputMode="decimal"` and the filter keeps `.`; otherwise
     * `inputMode="numeric"` and only digits survive.
     */
    allowDecimal?: boolean;
    /**
     * Trailing adornment slot — the unit/decoration shown at the inline-end
     * of the field (e.g. a "%" label, a "USDv" token chip, or an inline
     * unit `Select`). Domain content enters here as a slot, so this control
     * never imports a domain unit.
     */
    adornment?: React.ReactNode;
    /** Placeholder shown when the field is empty (`value === null`). */
    placeholder?: string;
    /** Extend / override the bordered container chrome. */
    className?: string;
    /** Extend / override the input chrome. */
    inputClassName?: string;
    /** Demo affordance — force the hover VISUAL (darker border) on the
     *  container with no pointer present, for scripted sims. Mirrors
     *  `<Input forceHover>` (shared border recipe). Default `false` —
     *  existing callers unaffected. */
    forceHover?: boolean;
    /** Demo affordance — force the focus VISUAL (focus border) on. Wins over
     *  `forceHover`. Default `false`. */
    forceFocus?: boolean;
    /** Sim-only cursor target key, forwarded to the container so
     *  `useSimCursor().moveTo(key)` can travel to this field. Inert in
     *  production. */
    "data-sim-cursor-key"?: string;
}

/**
 * NumberField — a bordered numeric text input with one trailing adornment
 * slot. A flat `h-8 border border-line-base` container holds a
 * digit-filtered `<input>` (`font-numeric`) and an optional trailing
 * adornment (unit label, token chip, or inline unit select).
 *
 * Generic Component: it knows no Solomon/YaaS entity. It owns only digit
 * (or digit+decimal) filtering; **clamping and formatting stay in the
 * caller** — `onChange` emits the parsed number (or `null` for empty), and
 * `format` renders the display text. The adornment is a slot, so this
 * control never imports a domain unit (dependency firewall). Composes a
 * bare `<input>` under the shared flat-field chrome.
 *
 * @version 1.0.0
 * @published 2026-07-04
 * @history
 *   1.0.0 — Initial publish. Extracted from the client Programs
 *           ProgramDetail page's hand-rolled numeric inputs (Rewards,
 *           Program Period, Base APY, Program Budget, Settlement Cycle) as
 *           a domain-free GC.
 *
 * @see NumberField.intent.md   policy + recipes
 * @see NumberField.layout.html intended layout
 */
export function NumberField({
    value,
    onChange,
    ariaLabel,
    format,
    allowDecimal = false,
    adornment,
    placeholder,
    className,
    inputClassName,
    forceHover = false,
    forceFocus = false,
    "data-sim-cursor-key": simCursorKey
}: NumberFieldProps): React.ReactElement {
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const filtered = event.target.value.replace(
            allowDecimal ? /[^0-9.]/g : /[^0-9]/g,
            ""
        );
        if (filtered === "") {
            onChange(null);
            return;
        }
        const parsed = Number(filtered);
        onChange(Number.isNaN(parsed) ? null : parsed);
    };

    return (
        <div
            data-sim-cursor-key={simCursorKey}
            className={cn(
                "flex items-center gap-2 h-8 w-full border border-line-base bg-transparent pl-2.5 pr-2",
                className,
                // Forced demo hover/focus (sim): literal border tokens so the
                // container border darkens without a pointer. Last so it wins
                // the border cascade against the base `border-line-base`.
                forcedFieldBorderClasses(forceHover, forceFocus)
            )}
        >
            <input
                inputMode={allowDecimal ? "decimal" : "numeric"}
                value={
                    value === null ? "" : format ? format(value) : String(value)
                }
                placeholder={placeholder}
                onChange={handleChange}
                aria-label={ariaLabel}
                className={cn(
                    "flex-1 min-w-0 bg-transparent outline-none font-numeric text-base text-foreground-base placeholder:text-foreground-neutral",
                    inputClassName
                )}
            />
            {adornment}
        </div>
    );
}
